import React, { useEffect, useRef } from 'react';

interface LiquidBackgroundProps {
  scrollProgress?: number;
}

export const LiquidBackground: React.FC<LiquidBackgroundProps> = ({ scrollProgress = 0 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const progressRef = useRef(scrollProgress);
  const timeRef = useRef(0);

  useEffect(() => {
    progressRef.current = scrollProgress;
  }, [scrollProgress]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      // We limit dpr to 2 to save performance on high-res screens
      const scale = Math.min(dpr, 2); 
      canvas.width = window.innerWidth * scale;
      canvas.height = window.innerHeight * scale;
      ctx.scale(scale, scale);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };
    window.addEventListener('resize', resize);
    resize();

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      baseRadius: number;
      color: string;
      phase: number;

      constructor() {
        this.x = Math.random() * window.innerWidth;
        this.y = Math.random() * window.innerHeight;
        
        // Slower, more viscous movement
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        
        // Distinct sizes: Some big, some small to create "splitting" effect visual interest
        const isLarge = Math.random() > 0.7;
        this.baseRadius = isLarge ? Math.random() * 40 + 40 : Math.random() * 20 + 15;
        this.radius = this.baseRadius;
        
        this.phase = Math.random() * Math.PI * 2;

        // LUXURY PALETTE: Distinct Gold, Amber, and Deep Oil
        const palette = [
            'rgba(251, 191, 36, 0.9)', // Amber-400 (Gold)
            'rgba(217, 119, 6, 0.8)',  // Amber-600 (Darker Gold)
            'rgba(120, 53, 15, 0.85)', // Amber-900 (Deep Brown/Oil)
            'rgba(252, 211, 77, 0.9)'  // Amber-300 (Light Gold)
        ];
        this.color = palette[Math.floor(Math.random() * palette.length)];
      }

      update(time: number) {
        // 1. Organic Float (Sine wave perturbation)
        this.x += this.vx + Math.sin(time * 0.001 + this.phase) * 0.2;
        this.y += this.vy + Math.cos(time * 0.001 + this.phase) * 0.2;

        // 2. Parallax Scroll Effect
        // Move slightly opposite to scroll to give depth
        const flow = progressRef.current;
        this.x -= flow * 2;

        // 3. Gentle Pulsing Size
        // Makes the drops feel alive
        this.radius = this.baseRadius + Math.sin(time * 0.002 + this.phase) * 2;

        // 4. Boundary Logic (Bounce instead of wrap for better "container" feel)
        const padding = this.radius;
        
        if (this.x < -padding) this.x = window.innerWidth + padding;
        if (this.x > window.innerWidth + padding) this.x = -padding;

        if (this.y < -padding) this.y = window.innerHeight + padding;
        if (this.y > window.innerHeight + padding) this.y = -padding;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
    }

    const init = () => {
      particles = [];
      // Fewer particles for distinct "drops", not a crowd
      // 12-15 is a sweet spot for desktop
      const count = window.innerWidth < 768 ? 8 : 15;
      for (let i = 0; i < count; i++) {
        particles.push(new Particle());
      }
    };

    const animate = (time: number) => {
      timeRef.current = time;
      if (!ctx) return;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      
      particles.forEach(p => {
        p.update(time);
        p.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-0">
      <svg className="hidden">
        <defs>
          <filter id="goo-refined">
            {/* 
              Tighter Blur: stdDeviation="12" (was 35). 
              This prevents the "cloud/blob" look and keeps shapes defined. 
            */}
            <feGaussianBlur in="SourceGraphic" stdDeviation="12" result="blur" />
            
            {/* 
              High Contrast Matrix:
              The last row `19 -9` is critical. It takes the alpha channel, multiplies it by 19, 
              then subtracts 9. This creates a hard edge where the blur fades out, 
              simulating surface tension.
            */}
            <feColorMatrix 
              in="blur" 
              mode="matrix" 
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9" 
              result="goo" 
            />
            <feComposite in="SourceGraphic" in2="goo" operator="atop"/>
          </filter>
        </defs>
      </svg>
      {/* Reduced opacity slightly to blend better with background */}
      <canvas 
        ref={canvasRef} 
        style={{ filter: 'url(#goo-refined)' }}
        className="w-full h-full opacity-80" 
      />
    </div>
  );
};